import { decodeFunctionResult, encodeFunctionData, keccak256, zeroAddress, type Abi, type Address, type Hex } from "viem";
import { MERKLE_V4, type MerkleV4Config } from "../config/merkle-v4";
import { sameTradeAddress as same, assertTradeConfig, isMerkleV4, tradeConfigKey } from "../config/trade";
import { encodeMerkleApproval, encodeMerkleSwap, encodeQuoteProbe, readProbeResult, MAX128, MAX_V4_INPUT,
  hookAbi, routerAbi, stateViewAbi, tokenAbi, vaultAbi, permit2Abi } from "../contracts/merkle-v4";
import { minimumSwapOutput, type SwapSide } from "../contracts/native-v3-trade";
import type { ReviewedSwap, TradeQuotePreview, TradeReadContext, SwapPolicy } from "./native-v3-trade";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { contractReadError } from "../contracts/contract-errors";

const quantity = (value: unknown): bigint => {
  if (typeof value !== "string" || !/^0x[0-9a-f]+$/i.test(value)) throw new Error("INVALID_RPC_QUANTITY");
  return BigInt(value);
};
function reader(provider: Eip1193Provider, guard: () => void, options: RpcReadOptions) {
  return async (to: Address, abi: Abi, functionName: string, args: readonly unknown[] = [], block = "latest") => {
    guard(); options.signal?.throwIfAborted();
    const raw = await readRpc(provider, { method: "eth_call", params: [{ to, data: encodeFunctionData({ abi, functionName, args }) }, block] }, options);
    guard(); options.signal?.throwIfAborted();
    if (typeof raw !== "string") throw new Error("INVALID_V4_READ");
    return decodeFunctionResult({ abi, functionName, data: raw as Hex });
  };
}
async function readMerkleHead(provider: Eip1193Provider, guard: () => void, options: RpcReadOptions, C: MerkleV4Config) {
  guard(); options.signal?.throwIfAborted(); assertTradeConfig(C);
  const [chain, raw] = await Promise.all([
    readRpc(provider, { method: "eth_chainId" }, options),
    readRpc(provider, { method: "eth_getBlockByNumber", params: ["latest", false] }, options),
  ]);
  guard();
  if (quantity(chain) !== BigInt(C.chainId)) throw new Error("ELYSIUM_CHAIN_MISMATCH");
  const h = raw as { number?: string; hash?: Hex; timestamp?: string } | null;
  if (!h?.hash || !/^0x[0-9a-f]{64}$/i.test(h.hash)) throw new Error("INVALID_V4_HEAD");
  const number = quantity(h.number), timestamp = quantity(h.timestamp);
  if (number < C.deploymentBlock || Math.abs(Date.now() / 1000 - Number(timestamp)) > 30) throw new Error("STALE_HEAD");
  return { blockNumber: number, blockHash: h.hash, timestamp, block: `0x${number.toString(16)}` };
}
async function verifyMerkleDeployment(provider: Eip1193Provider, block: string, guard: () => void, options: RpcReadOptions, C: MerkleV4Config) {
  guard(); options.signal?.throwIfAborted();
  const read = reader(provider, guard, options);
  const [codes, bindings] = await Promise.all([
    Promise.all([C.router, C.permit2].map(address => readRpc(provider, { method: "eth_getCode", params: [address, block] }, options))),
    Promise.all([
      read(C.router, routerAbi, "poolManager", [], block), read(C.stateView, stateViewAbi, "poolManager", [], block),
      read(C.hook, hookAbi, "POOL_MANAGER", [], block), read(C.hook, hookAbi, "SOURCE_TOKEN", [], block),
      read(C.hook, hookAbi, "FEE_VAULT", [], block), read(C.vault, vaultAbi, "sourceV4FeeHook", [C.token], block),
      read(C.hook, hookAbi, "FEE_BPS", [], block), read(C.hook, hookAbi, "TICK_SPACING", [], block),
    ]),
  ]);
  guard(); options.signal?.throwIfAborted();
  for (const [i, expected] of [C.routerHash, C.permit2Hash].entries()) {
    const code = codes[i];
    if (typeof code !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(code) || keccak256(code as Hex) !== expected) throw new Error("V4_RUNTIME_CHANGED");
  }
  const addresses = [C.poolManager, C.poolManager, C.poolManager, C.token, C.vault, C.hook];
  if (addresses.some((a, i) => typeof bindings[i] !== "string" || !same(bindings[i] as string, a)) || bindings[6] !== 300n || bindings[7] !== 1)
    throw new Error("V4_BINDING_MISMATCH");
}
export async function observeMerkleV4(provider: Eip1193Provider, guard: () => void, options: RpcReadOptions = {}, C: MerkleV4Config = MERKLE_V4) {
  const head = await readMerkleHead(provider, guard, options, C);
  await verifyMerkleDeployment(provider, head.block, guard, options, C);
  return head;
}
async function confirmHead(provider: Eip1193Provider, head: Awaited<ReturnType<typeof observeMerkleV4>>, guard: () => void, options: RpcReadOptions) {
  const h = await readRpc(provider, { method: "eth_getBlockByNumber", params: [head.block, false] }, options) as { hash?: string } | null;
  guard(); options.signal?.throwIfAborted();
  if (!same(h?.hash, head.blockHash)) throw new Error("QUOTE_REORG");
}
async function walletState(provider: Eip1193Provider, account: Address, deadline: bigint, guard: () => void, options: RpcReadOptions, C: MerkleV4Config, side: SwapSide, block = "latest") {
  const read = reader(provider, guard, options);
  const [native, token, allowances] = await Promise.all([
    readRpc(provider, { method: "eth_getBalance", params: [account, block] }, options),
    read(C.token, tokenAbi, "balanceOf", [account], block),
    side === "SELL" ? Promise.all([
      read(C.token, tokenAbi, "allowance", [account, C.permit2], block),
      read(C.permit2, permit2Abi, "allowance", [account, C.token, C.router], block),
    ]) : null,
  ]);
  guard();
  if (typeof token !== "bigint") throw new Error("INVALID_V4_WALLET_STATE");
  // Allowances are unused for native BUY; zero here does not represent an RPC observation.
  let ercAllowance = 0n, permitAllowance = 0n;
  if (allowances) {
    const [erc, permit] = allowances;
    if (typeof erc !== "bigint" || !Array.isArray(permit) || typeof permit[0] !== "bigint") throw new Error("INVALID_V4_WALLET_STATE");
    ercAllowance = erc;
    permitAllowance = BigInt(permit[1]) >= deadline ? permit[0] : 0n;
  }
  return { nativeBalance: quantity(native), tokenBalance: token, ercAllowance, permitAllowance,
    allowance: ercAllowance < permitAllowance ? ercAllowance : permitAllowance };
}
type Input = { account: Address | null; side: SwapSide; maximum: bigint; slippageBps: number };
type Options = RpcReadOptions & { approvalBlock?: { number: bigint; hash: string }; onPreview?: (value: TradeQuotePreview) => void };
export async function readMerkleQuote(provider: Eip1193Provider, input: Input, guard: () => void, options: Options = {}, C: MerkleV4Config = MERKLE_V4): Promise<ReviewedSwap | TradeQuotePreview> {
  if (input.maximum <= 0n || input.maximum > MAX_V4_INPUT) throw new Error("INVALID_V4_AMOUNT");
  const started = performance.now(), head = await observeMerkleV4(provider, guard, options, C);
  if (options.approvalBlock && (head.blockNumber < options.approvalBlock.number || head.blockNumber === options.approvalBlock.number && !same(head.blockHash, options.approvalBlock.hash))) throw new Error("RPC has not caught up to approval. Refresh quote.");
  const deadline = head.timestamp + 300n, read = reader(provider, guard, options);
  const probe = async (output: boolean) => {
    guard();
    try {
      await readRpc(provider, { method: "eth_call", params: [{ from: input.account ?? zeroAddress, to: C.router, value: "0x0",
        data: encodeQuoteProbe(input.side, input.maximum, deadline, output, C) }, head.block] }, options);
    } catch (error) { guard(); options.signal?.throwIfAborted(); return readProbeResult(error, output); }
    throw new Error("PROBE_UNEXPECTED_SUCCESS");
  };
  const [amountOut, amountIn, slot] = await Promise.all([probe(true), probe(false), read(C.stateView, stateViewAbi, "getSlot0", [C.poolId], head.block)]);
  guard();
  if (amountIn !== input.maximum || amountOut <= 0n || amountOut >= MAX128 || !Array.isArray(slot) || typeof slot[0] !== "bigint" || slot[0] <= 0n) throw new Error("PARTIAL_OR_EMPTY_QUOTE");
  // SELL fee is known from the receipt, not exactly recoverable from the net-only probe.
  const quote = { amountIn, amountOut, feeNative: input.side === "BUY" ? amountIn * 300n / 10000n : null };
  const preview: TradeQuotePreview = { side: input.side, maximum: amountIn, minimum: minimumSwapOutput(quote, amountIn, input.slippageBps),
    priceLimit: 0n, quote, sqrtPriceX96: slot[0], slippageBps: input.slippageBps, blockNumber: head.blockNumber, blockHash: head.blockHash, expiresAt: started + 30000 };
  await confirmHead(provider, head, guard, options);
  if (performance.now() >= preview.expiresAt) throw new Error("QUOTE_EXPIRED");
  options.onPreview?.(preview);
  if (!input.account) return preview;
  const state = await walletState(provider, input.account, deadline, guard, options, C, input.side, head.block);
  await confirmHead(provider, head, guard, options);
  if (performance.now() >= preview.expiresAt) throw new Error("QUOTE_EXPIRED");
  return { ...preview, ...state, account: input.account, recipient: input.account, id: crypto.randomUUID(), feeBps: 300, deadline,
    approvalStep: input.side === "BUY" ? null : state.ercAllowance < amountIn ? "TOKEN" : state.permitAllowance < amountIn ? "PERMIT2" : null,
    approvalExpiration: head.timestamp + 1800n, protocol: "MERKLE_V4", configurationKey: tradeConfigKey(C) };
}
export async function prepareMerkleSend(provider: Eip1193Provider, quote: ReviewedSwap, kind: "APPROVAL" | "SWAP",
  context: () => TradeReadContext, policy: SwapPolicy, guard: () => void, options: RpcReadOptions = {}) {
  const C = context().trade;
  if (!C || !isMerkleV4(C)) throw new Error("MERKLE_V4_CONFIGURATION_UNAVAILABLE");
  const originalKey = tradeConfigKey(C);
  if (quote.configurationKey !== originalKey) throw new Error("TRADE_CONTEXT_CHANGED");
  const check = () => {
    guard(); options.signal?.throwIfAborted();
    const trade = context().trade;
    if (!trade || !isMerkleV4(trade)) throw new Error("MERKLE_V4_DEPLOYMENT_MISMATCH");
    assertTradeConfig(trade);
    if (tradeConfigKey(trade) !== originalKey) throw new Error("TRADE_CONTEXT_CHANGED");
    if (!policy.enabled || quote.protocol !== "MERKLE_V4" || performance.now() >= quote.expiresAt) throw new Error("Refresh the quote and review again.");
  };
  check();
  if (kind === "APPROVAL" && (quote.side !== "SELL" || !quote.approvalStep || !quote.approvalExpiration))
    throw new Error("APPROVAL_CHANGED_REFRESH_QUOTE");
  const tx = kind === "APPROVAL" ? encodeMerkleApproval(quote.maximum, quote.approvalStep!, quote.approvalExpiration!, C) : encodeMerkleSwap(quote, C);
  const call = { from: quote.account, to: tx.to, data: tx.data, value: `0x${tx.valueWei.toString(16)}` };
  // Reviewed calldata is already known. Queue all independent latest-state
  // checks together so the configured transport sends one HTTP batch.
  const [head, state, [result, gas, gasPrice]] = await Promise.all([
    readMerkleHead(provider, check, options, C),
    walletState(provider, quote.account, quote.deadline, check, options, C, quote.side),
    Promise.all([
      readRpc(provider, { method: "eth_call", params: [call, "latest"] }, options),
      readRpc(provider, { method: "eth_estimateGas", params: [call] }, options),
      readRpc(provider, { method: "eth_gasPrice" }, options),
    ]).catch(error => {
      check();
      throw new Error(error instanceof Error ? error.message : "Transaction simulation failed", { cause: contractReadError(error) });
    }),
  ]);
  check();
  if (head.timestamp >= quote.deadline) throw new Error("SWAP_DEADLINE_EXPIRED");
  if (kind === "APPROVAL" && quote.approvalExpiration! <= head.timestamp) throw new Error("APPROVAL_CHANGED_REFRESH_QUOTE");
  if (quote.side === "SELL" && state.tokenBalance < quote.maximum) throw new Error("INSUFFICIENT_TOKEN_BALANCE");
  if (kind === "SWAP" && quote.side === "SELL" && state.allowance < quote.maximum) throw new Error("APPROVAL_REQUIRED");
  if (kind === "APPROVAL" && (state.ercAllowance < quote.maximum ? "TOKEN" : state.permitAllowance < quote.maximum ? "PERMIT2" : null) !== quote.approvalStep)
    throw new Error("APPROVAL_CHANGED_REFRESH_QUOTE");
  let gasLimit: bigint;
  try {
    check();
    if (kind === "APPROVAL" && quote.approvalStep === "TOKEN") {
      if (typeof result !== "string" || decodeFunctionResult({ abi: tokenAbi, functionName: "approve", data: result as Hex }) !== true) throw new Error("APPROVAL_SIMULATION_FAILED");
    } else if (result !== "0x") throw new Error("INVALID_V4_SIMULATION");
    gasLimit = (quantity(gas) * 120n + 99n) / 100n;
    if (gasLimit <= 0n || quantity(gasPrice) <= 0n || state.nativeBalance < tx.valueWei + gasLimit * quantity(gasPrice))
      throw new Error("INSUFFICIENT_NATIVE_FOR_VALUE_AND_GAS");
  } catch (error) {
    check();
    throw new Error(error instanceof Error ? error.message : "Transaction simulation failed", { cause: contractReadError(error) });
  }
  check(); return { ...tx, gas: gasLimit, assertCurrent: check };
}
